import { IFocusableItemProps } from "@components/focusManager";
import { Month } from "@components/inputs/date/popup/Month";
import { isDefined } from "@utils/general";
import { Dayjs } from "dayjs";
import React from "react";

import { DatePickerView } from "../../../../enums";
import { getUtcDayjs } from "../../../../types/Date";
import {
    dateIntervalToDayjsInterval,
    dayjsIntervalToDateInterval,
    IDayInterval,
    IDayjsInterval,
    isSameDay,
    isSameMonth,
    isValidDateInterval
} from "../utils";
import { Calendar } from "./Calendar";
import { IDateRangeValuePopupSharedProps, TDisabledDateUnit } from "./Calendar.utils";

interface IState {
    view: DatePickerView;
    // not only mouse, but keyboard focus also
    mouseOverMonth: Dayjs;
    value: IDayjsInterval;
    previewDay?: Dayjs;
}

export class MonthYearRangePopup extends React.PureComponent<IDateRangeValuePopupSharedProps, IState> {
    constructor(props: IDateRangeValuePopupSharedProps) {
        super(props);

        const val = this.props.value as IDayInterval;
        this.state = {
            view: DatePickerView.Months,
            mouseOverMonth: null,
            value: this.props.value ? dateIntervalToDayjsInterval(val) : {
                from: null,
                to: null
            }
        };
    }

    componentDidUpdate(prevProps: Readonly<IDateRangeValuePopupSharedProps>) {
        const prevValue = prevProps.value as IDayInterval;

        if (isValidDateInterval(this.props.value) &&
                (this.props.value?.from !== prevValue?.from || this.props.value?.to !== prevValue?.to)) {
            this.setState({ value: dateIntervalToDayjsInterval(this.props.value) });
        }
    }

    handleCalendarChange = (day: Dayjs): void => {
        if (!day) {
            this.setState({
                value: null
            });

            return;
        }

        const value = this.state.value as IDayjsInterval;

        const shouldSelectFromDay = !value?.from
                || (value.from && value.to);

        if (shouldSelectFromDay) {
            this.setState({
                value: {
                    from: day,
                    to: null
                }
            });
        } else {
            // User selects second date, swaps them to correct order
            const swapInterval = value.from.isAfter(day);
            this.setState({
                value: {
                    from: swapInterval ? day : value.from,
                    to: (swapInterval ? value.from : day)
                }
            });
        }
    };

    isDaySelected = (day: Dayjs): boolean => {
        if (!this.state.value) {
            return false;
        }

        const value = this.state.value as IDayjsInterval;

        return isSameDay(day, value.from) || isSameDay(day, value.to);
    };

    isInActiveRange = (day: Dayjs): boolean => {
        const value = this.state.value as IDayjsInterval;

        if (!this.state.value || !value.from || value.to || !this.state.mouseOverMonth) {
            return false;
        }

        const { from } = value;
        const to = this.state.mouseOverMonth;
        const swapInterval = from?.isAfter(to);

        return this.isDaySelected(day) ||
                isSameDay(day, this.state.mouseOverMonth) ||
                day.isBetween(swapInterval ? to : from, swapInterval ? from : to);
    };

    isInSelectedRange = (day: Dayjs): boolean => {
        const value = this.state.value as IDayjsInterval;

        if (!this.state.value || !value.from || !value.to) {
            return false;
        }

        const { from, to } = value;

        return this.isDaySelected(day) || day.isBetween(from, to);
    };

    shouldPreventHover = (day: Dayjs): boolean => {
        return !!this.state.mouseOverMonth;
    };

    handleMouseEnterMonth = (day: Dayjs): void => {
        const value = this.state.value as IDayjsInterval;

        if (value?.from && !value.to) {
            this.setState({
                mouseOverMonth: day
            });
        }
    };

    handleMouseLeaveMonth = (day: Dayjs): void => {
        this.setState({
            mouseOverMonth: null
        });
    };

    isDisabledDate = (date: Dayjs, unit: TDisabledDateUnit): boolean => {
        if (!this.props.minDate && !this.props.maxDate && !this.props.isDateDisabled) {
            return false;
        }

        let isDisabled = false;

        if (this.props.minDate) {
            isDisabled = isDisabled || date.isBefore(this.props.minDate, unit);
        }

        if (this.props.maxDate) {
            isDisabled = isDisabled || date.isAfter(this.props.maxDate, unit);
        }

        if (this.props.isDateDisabled) {
            isDisabled = isDisabled || this.props.isDateDisabled(date.toDate(), unit);
        }

        return isDisabled;
    };

    isMonthSelected = (date: Dayjs): boolean => {
        return (isDefined(this.state.value?.from) && getUtcDayjs(this.state.value.from).isSame(date, "month")) ||
                (isDefined(this.state.value?.to) && getUtcDayjs(this.state.value.to).isSame(date, "month"));
    };

    handleMonthClick = (date: Dayjs): void => {
        this.handleCalendarChange(date);
    };

    renderMonth = (date: Dayjs, index: number, itemProps: IFocusableItemProps): React.ReactNode => {
        const isSelected = this.isMonthSelected(date);

        const isDisabled = this.isDisabledDate(date, "month");

        const value = this.state.value as IDayjsInterval;

        const { from, to } = value ?? {};
        const swapInterval = !to && this.state.mouseOverMonth && from?.isAfter(this.state.mouseOverMonth);
        const isFirst = isSameMonth(date.toDate(), from?.toDate());
        const isLast = isSameMonth(date.toDate(), to?.toDate()) || isSameMonth(date.toDate(), this.state.mouseOverMonth?.toDate());

        return (
                <Month date={date}
                       isFirst={swapInterval ? isLast : isFirst}
                       isLast={swapInterval ? isFirst : isLast}
                       isSelected={isSelected}
                       isDisabled={isDisabled}
                       isInActiveRange={this.isInActiveRange(date)}
                       isInSelectedRange={this.isInSelectedRange(date)}
                       onMouseEnter={this.handleMouseEnterMonth}
                       onMouseLeave={this.handleMouseLeaveMonth}
                       onClick={this.handleMonthClick}
                       itemProps={itemProps}
                />
        );
    };

    handleOk = (): void => {
        if (!this.state.value) {
            this.props.onChange(null);
            return;
        }

        const value = this.state.value as IDayjsInterval;
        let { from, to } = value;

        if (from.isAfter(to)) {
            from = to;
            to = value.from;
        }

        this.props.onChange(dayjsIntervalToDateInterval({
            from: from.startOf("month"),
            to: to.startOf("month")
        }));
    };

    handleCancel = (): void => {
        this.props.onChange(this.props.value);
    };

    handlePreviewChange = (day: Dayjs): void => {
        this.setState({ previewDay: day });
    };

    render = (): React.ReactElement => {
        const value = this.state.value as IDayjsInterval;

        const disabledOk = this.state.value && (!value.from || !value.to);

        return <Calendar view={this.state.view}
                         previewDay={this.state.previewDay}
                         mainView={DatePickerView.Months}
                         onKeyDown={this.props.onKeyDown}
                         renderMonth={this.renderMonth}
                         onPreviewChange={this.handlePreviewChange}
                         disableAutoFocus={this.props.disableAutoFocus}
                         minDate={this.props.minDate}
                         maxDate={this.props.maxDate}
                         isDateDisabled={this.props.isDateDisabled}
                         hideHeader={this.props.hideHeader}
                         disableViewChange
                         showConfirmButtons
                         disabledOk={disabledOk}
                         onOk={this.handleOk}
                         onCancel={this.handleCancel}
        />;
    };
}